import { Helmet } from "react-helmet-async";

interface SEOProps {
  // Basic SEO
  title: string;
  description: string;
  canonical?: string;
  keywords?: string[];
  
  // Open Graph
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogType?: string;
  ogUrl?: string;
  
  // Article metadata
  articlePublishedTime?: string;
  articleAuthor?: string;
  articleSection?: string;
  articleTags?: string[];
  
  // Twitter Card
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  
  // Schema and geo (existing)
  schema?: object;
  hreflang?: string;
  region?: string;
  localAreas?: string[];
}

const SEO = ({
  title,
  description,
  canonical,
  keywords,
  ogTitle,
  ogDescription,
  ogImage = "https://storage.googleapis.com/gpt-engineer-file-uploads/xpc6m3MSVoUVwqOVjN2erqrA9O42/social-images/social-1759977509538-FOX2.png",
  ogImageAlt,
  ogImageWidth,
  ogImageHeight,
  ogType = "website",
  ogUrl,
  articlePublishedTime,
  articleAuthor,
  articleSection,
  articleTags = [],
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  twitterImage,
  schema,
  hreflang = "en-gb",
  region = "GB",
  localAreas = [],
}: SEOProps) => {
  return (
    <Helmet>
      {/* Basic SEO */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Geo-targeting */}
      <meta name="geo.region" content={region} />
      {region === "GB" && <meta name="geo.placename" content="United Kingdom" />}
      {localAreas.map((area) => (
        <meta key={area} name="geo.placename" content={area} />
      ))}
      
      {/* Language targeting */}
      {canonical && (
        <>
          <link rel="alternate" hrefLang={hreflang} href={canonical} />
          <link rel="alternate" hrefLang="x-default" href={canonical} />
        </>
      )}
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:image" content={ogImage} />
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      {ogImageWidth && <meta property="og:image:width" content={String(ogImageWidth)} />}
      {ogImageHeight && <meta property="og:image:height" content={String(ogImageHeight)} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={ogUrl || canonical || "https://www.4fox.uk/"} />
      <meta property="og:locale" content={hreflang === "en-gb" ? "en_GB" : "en_US"} />
      
      {/* Article-specific Open Graph */}
      {articlePublishedTime && (
        <meta property="article:published_time" content={articlePublishedTime} />
      )}
      {articleAuthor && (
        <meta property="article:author" content={articleAuthor} />
      )}
      {articleSection && (
        <meta property="article:section" content={articleSection} />
      )}
      {articleTags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle || ogTitle || title} />
      <meta name="twitter:description" content={twitterDescription || ogDescription || description} />
      <meta name="twitter:image" content={twitterImage || ogImage} />
      
      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
